import { format, parseISO } from 'date-fns'

export const PAGE_SIZE = 20

export const RECIPIENTS = [
  { code: 'ALTEZZA_TRAVELLING_LTD', name: 'Altezza Travelling Ltd' },
  { code: 'ALTURA_EXPEDITIONS', name: 'Altura Expeditions' },
]

export const STATUSES = [
  { code: 'draft', name: 'Draft' },
  { code: 'confirmed', name: 'Confirmed' },
  { code: 'cancelled', name: 'Cancelled' },
]

export function tableOptions(headings, perPage = PAGE_SIZE) {
  return {
    headings,
    filterable: false,
    pagination: { show: false },
    sortable: [],
    perPage,
    texts: { noResults: 'No matching entries' },
    rowClassCallback: () => 'table-default__row',
    resizableColumns: false,
  }
}

export function formatDate(value) {
  return value ? format(parseISO(value), 'dd MMM yyyy') : '-'
}

export function formatAmount(value, currency = '') {
  if (value === null || value === undefined || value === '') return '-'
  const [integer, fraction = ''] = String(value).split('.')
  const amount = `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction.padEnd(2, '0')}`
  return currency ? `${amount} ${currency}` : amount
}

export function recipientName(recipient) {
  if (recipient && typeof recipient === 'object') return recipient.name
  const match = RECIPIENTS.find(item => item.code === recipient)
  return match ? match.name : recipient || '-'
}

export function statusName(status) {
  const match = STATUSES.find(item => item.code === status)
  return match ? match.name : status
}

export function groupId(group) {
  const id = Number(group && typeof group === 'object' ? group.id : group)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

export function groupName(group) {
  return group && typeof group === 'object' ? group.name || `Group ${group.id}` : group || '-'
}

// Keep decimal API amounts exact when summing or comparing funding totals.
export function minorUnits(value) {
  if (!/^\d+(?:\.\d{1,2})?$/.test(String(value))) return null
  const [integer, fraction = ''] = String(value).split('.')
  return (BigInt(integer) * BigInt(100)) + BigInt(fraction.padEnd(2, '0'))
}

export function positiveAmount(value) {
  const amount = minorUnits(value)
  return /^\d{1,16}(?:\.\d{1,2})?$/.test(String(value)) && amount !== null && amount > BigInt(0)
}

export function sumFunding(rows, field = 'available_funding_amount') {
  const total = rows.reduce((sum, row) => sum + (row.bookings && field === 'amount_usd' ? row.bookings.filter(b => b.selected).reduce((n, b) => n + (minorUnits(b.amount_usd) || BigInt(0)), BigInt(0)) : (minorUnits(row[field]) || BigInt(0))), BigInt(0))
  return `${total / BigInt(100)}.${String(total % BigInt(100)).padStart(2, '0')}`
}

export function canSelect(row) {
  return row.availability === 'available' && groupId(row.group) !== null && positiveAmount(row.available_funding_amount) && !!row.currency
}

export function availabilityLabel(row) {
  if (row.availability === 'available') return 'Available'
  if (row.unavailable_reason === 'fully_funded') return 'Already funded'
  if (row.unavailable_reason === 'reserved_in_draft') return 'Reserved in draft'
  return 'Unavailable'
}

export function availabilityReason(row) {
  if (row.availability === 'available') return ''
  if (row.unavailable_reason === 'reserved_in_draft') {
    const batch = row.active_draft_batch
    return batch && typeof batch === 'object' ? batch.number || '' : batch || ''
  }
  const reasons = {
    fully_funded: 'Funding requirement is fully covered',
    story_cost_missing: 'Story cost is missing',
    normalization_not_valid: 'Normalization is not valid',
    unsupported_currency: 'Unsupported currency',
    non_positive_residual: 'No positive story residual',
    usd_normalization_required: 'USD normalization is required',
    funding_estimate_missing: 'Funding estimate is missing',
    non_positive_funding_base: 'No positive funding requirement',
    permit_estimate_missing: 'Permit estimate is missing',
    permit_estimate_stale: 'Permit estimate is outdated',
    permit_estimate_blocked: 'Permit estimate is blocked',
    coverage_missing: 'No payment coverage for this payer',
    coverage_not_calculated: 'Payment coverage has not been calculated',
    coverage_needs_review: 'Payment coverage needs review',
    coverage_exhausted: 'Payment coverage is exhausted',
    coverage_deficit: 'Payment coverage has a deficit',
    funding_need_deficit: 'Funding exceeds the current booking requirement',
    membership_changed: 'Participant membership changed; reconciliation is required',
    legacy_funding_unresolved: 'Historical funding must be reconciled',
    relationship_inactive: 'Funding direction is inactive',
    workflow_disabled: 'New Altezza funding is not enabled yet',
  }
  return reasons[row.unavailable_reason] || row.unavailable_reason || ''
}

// Decimal(18,2) storage capacity, independent of the optional proposal budget.
export function fundingTotalValid(value) {
  const amount = minorUnits(value)
  return amount !== null && amount <= BigInt('999999999999999999')
}

export function fundingSelectionValid(rows) {
  return rows.length > 0 && rows.every(canSelect) && new Set(rows.map(row => row.currency)).size === 1 &&
    new Set(rows.map(row => groupId(row.group))).size === rows.length && fundingTotalValid(sumFunding(rows))
}

export function relationshipName(relationship) {
  return relationship ? `${relationship.payer_entity.name} → ${relationship.recipient.name}` : 'Historical payment — direction not assigned'
}

export function directionParams(relationship) {
  if (relationship) return { relationship }
  throw new Error('A funding relationship is required')
}

export function batchRecipient(batch) {
  return batch.recipient && typeof batch.recipient === 'object' ? batch.recipient.code : batch.recipient
}

export function isAltezzaBatch(batch) {
  return batchRecipient(batch) === 'ALTEZZA_TRAVELLING_LTD'
}

export function fundingLines(rows) {
  return rows.filter(row => !row.bookings || row.bookings.some(b => b.selected)).map(row => row.bookings ? {
    climbing_group_id: groupId(row.group),
    bookings: row.bookings.filter(b => b.selected).map(b => ({ client_group_id: b.client_group_id, amount_usd: b.amount_usd })),
  } : { client_group_id: groupId(row.group), amount_usd: row.amount_usd })
}

export function decimalAmount(cents) {
  return `${cents / BigInt(100)}.${String(cents % BigInt(100)).padStart(2, '0')}`
}

// A coverage can pay for several climbing groups, but can only be spent once.
export function selectedRows(rows, existing = []) {
  const used = new Map()
  existing.forEach(row => (row.bookings || []).filter(b => b.selected).forEach(b => {
    used.set(b.coverage_id, (used.get(b.coverage_id) || BigInt(0)) + (minorUnits(b.amount_usd) || BigInt(0)))
  }))
  return rows.map(row => ({
    ...row,
    amount_usd: row.available_funding_amount,
    ...(row.bookings ? {
      bookings: row.bookings.map(b => {
        const available = minorUnits(typeof b.selected === 'boolean' ? b.amount_usd : b.available_funding_amount) || BigInt(0)
        const free = (minorUnits(b.coverage && b.coverage.free_usd) || BigInt(0)) - (used.get(b.coverage_id) || BigInt(0))
        const amount = b.selected !== false && b.availability === 'available' && free > BigInt(0) ? (free < available ? free : available) : BigInt(0)
        used.set(b.coverage_id, (used.get(b.coverage_id) || BigInt(0)) + amount)
        return { ...b, selected: amount > BigInt(0), amount_usd: decimalAmount(amount), original_amount: '0.00' }
      }),
    } : {}) }))
}

export function retainedRows(rows) {
  return rows.map(row => ({
    ...row,
    retained: true,
    amount_usd: row.funding_amount,
    ...(row.bookings && row.bookings.length ? {
      bookings: row.bookings.map(b => ({ ...b, selected: true, original_amount: b.amount_usd, availability: 'unavailable', unavailable_reason: 'permit_estimate_stale', available_funding_amount: '0.00' })),
    } : { bookings: undefined }) }))
}

export function bookingAmountError(booking, retained = false) {
  if (!booking.selected) return ''
  if (!positiveAmount(booking.amount_usd)) return 'Enter a positive amount with at most 2 decimal places'
  const amount = minorUnits(booking.amount_usd)
  if (retained && amount <= (minorUnits(booking.original_amount) || BigInt(0))) return ''
  if (booking.availability !== 'available') return availabilityReason(booking) || 'Booking is unavailable'
  return amount > (minorUnits(booking.available_funding_amount) || BigInt(0)) ? 'Amount exceeds available funding' : ''
}

export function coverageSelectionError(rows) {
  const amounts = new Map()
  const limits = new Map()
  const originals = new Map()
  rows.forEach(row => (row.bookings || []).forEach(b => {
    const id = b.coverage_id
    if (row.retained) originals.set(id, (originals.get(id) || BigInt(0)) + (minorUnits(b.original_amount) || BigInt(0)))
    const limit = minorUnits(b.coverage && b.coverage.free_usd) || BigInt(0)
    limits.set(id, limits.has(id) && limits.get(id) < limit ? limits.get(id) : limit)
    if (b.selected) amounts.set(id, (amounts.get(id) || BigInt(0)) + (minorUnits(b.amount_usd) || BigInt(0)))
  }))
  // Reductions remain available even if a saved coverage has since fallen.
  return Array.from(amounts).some(([id, amount]) => amount > (originals.get(id) || BigInt(0)) && amount > limits.get(id)) ? 'Selected bookings exceed their shared payer coverage' : ''
}

// Proposal results describe availability; lines carry the selected partial amounts.
export function proposalRows(proposal, partial) {
  if (!partial) return selectedRows(proposal.results)
  if (!Array.isArray(proposal.lines) || proposal.lines.length !== proposal.results.length) return []
  if (proposal.lines.some(line => line.climbing_group_id)) {
    const rows = proposal.lines.map(line => {
      const row = proposal.results.find(item => groupId(item.group) === line.climbing_group_id)
      if (!row || !row.bookings || !Array.isArray(line.bookings)) return null
      const selected = new Map(line.bookings.map(b => [b.client_group_id, b.amount_usd]))
      if (selected.size !== line.bookings.length || line.bookings.some(b => !row.bookings.some(item => item.client_group_id === b.client_group_id))) return null
      return { ...row, bookings: row.bookings.map(b => ({ ...b, selected: selected.has(b.client_group_id), amount_usd: selected.get(b.client_group_id) || b.available_funding_amount, original_amount: '0.00' })) }
    })
    return rows.every(Boolean) && new Set(rows.map(row => groupId(row.group))).size === rows.length ? rows : []
  }
  const ids = proposal.lines.map(line => line.client_group_id)
  if (new Set(ids).size !== ids.length) return []
  const rows = proposal.lines.map(line => {
    const row = proposal.results.find(item => groupId(item.group) === line.client_group_id)
    return row ? { ...row, amount_usd: line.amount_usd } : null
  }).filter(Boolean)
  return rows.length === proposal.lines.length ? rows : []
}

export function lineAmountError(row, partial, historical = false) {
  if (row.bookings) return row.bookings.map(b => bookingAmountError(b, row.retained)).find(Boolean) || ''
  if (!positiveAmount(row.amount_usd)) return 'Enter a positive amount with at most 2 decimal places'
  const amount = minorUnits(row.amount_usd)
  if (row.retained) {
    if (!partial) return amount === minorUnits(row.funding_amount) ? '' : 'Altura funding amounts cannot be changed'
    // Releases and unchanged historical rows remain possible with obsolete snapshots.
    if (amount <= minorUnits(row.funding_amount)) return ''
    if (historical) return 'Historical funding must be reconciled before increasing amounts'
    const base = minorUnits(row.funding_base_snapshot)
    const funded = minorUnits(row.funded_before_snapshot)
    if (partial && (base === null || funded === null || amount > base - funded)) return 'Amount exceeds the saved remaining requirement'
    // The API checks current eligibility and coverage, including our reservation.
    return ''
  }
  if (!canSelect(row)) return 'This tour is unavailable'
  const available = minorUnits(row.available_funding_amount)
  if (partial ? amount > available : amount !== available) return partial ? 'Amount exceeds available funding' : 'Use the full available amount'
  return ''
}

export function editableSelectionValid(rows, partial, draft = false, historical = false) {
  return (draft || fundingLines(rows).length > 0) && !coverageSelectionError(rows) &&
    rows.every(row => groupId(row.group) !== null && !!row.currency && !lineAmountError(row, partial, historical)) &&
    new Set(rows.map(row => groupId(row.group))).size === rows.length &&
    new Set(rows.map(row => row.currency)).size <= 1 &&
    fundingTotalValid(sumFunding(rows, 'amount_usd'))
}

export function proposalSelectionValid(rows, budget, partial) {
  return positiveAmount(budget) && editableSelectionValid(rows, partial) &&
    minorUnits(sumFunding(rows, 'amount_usd')) <= minorUnits(budget)
}
